// Bausteine der Deals-Liste (VertragsAmPendingBoard.jsx): Phasen, Filter,
// Auswahlleiste, Tabelle und der Dialog fuer die Pachtzahlen.
//
// Ueberarbeitet am 23.09.2026 im Stil des Abwaegens. Vorher:
// - Unter dem Titel stand die Liste der ueberwachten Bitrix-Phasen - dieselben
//   Phasen stehen als Filter direkt darunter.
// - Die Leiste fuer das Erstellen mehrerer Vertraege war immer sichtbar, auch
//   ohne Auswahl: acht Bedienelemente, dazu die feste Anzeige "Varianten:
//   CI + Druckversion" und ein Knopf "Sichtbare auswaehlen", der dasselbe tat
//   wie das Haekchen im Tabellenkopf.
// - Die Spalte hiess "Bulk", der Knopf je Zeile "Auswaehlen" - zu leicht mit
//   dem Ankreuzen zu verwechseln.
// Die Vertragswahl bleibt ohne Auswahl sichtbar: Sie entscheidet, welche Deals
// sich ueberhaupt ankreuzen lassen ("Automatisch je Deal" laesst Deals ohne
// erkennbare Vertragsart aus).

import React from 'react';
import {
  AlertCircle, ArrowRight, CheckCircle2, Download, Loader2, PlayCircle, Search, X,
} from 'lucide-react';
import { T, feldStil, knopfStil, primaerKnopfStil } from '../../lib/designTokens';
import { textKnopfStil } from './abwaegenAnzeige';

// Diese Phase heisst in Bitrix anders, als die Bearbeiter sie kennen.
const PHASEN_NAME = { 'C16:UC_V3GMKB': 'Vertrag prüfen und Versenden' };

export function phasenName(phaseId, bitrixName) {
  return PHASEN_NAME[phaseId] || bitrixName || '';
}

const kompaktesFeld = { ...feldStil, width: 'auto', padding: '7px 9px', fontSize: T.tSm, background: T.flaeche };

function aus(gesperrt) {
  return { opacity: gesperrt ? 0.5 : 1, cursor: gesperrt ? 'not-allowed' : 'pointer' };
}

/** Die Phasen als Filter: "Alle 23", "Vertrag erstellen (normal) 12" … */
export function PhasenFilter({ optionen, aktiv, onWahl }) {
  if (!optionen.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {optionen.map((phase) => {
        const an = aktiv === phase.phaseId;
        return (
          <button key={phase.phaseId} type="button" onClick={() => onWahl(phase.phaseId)} aria-pressed={an} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999,
            border: `1px solid ${an ? T.navy : T.rand}`, background: an ? T.navy : T.flaeche,
            color: an ? T.flaeche : T.body, fontSize: T.tXs, fontWeight: 600, cursor: 'pointer',
          }}>
            {phase.phaseLabel}
            <span style={{ fontWeight: 700, color: an ? '#C9D3E6' : T.label }}>{phase.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Suche, Verantwortlich, Art, Sortierung - eine Zeile, ohne Ueberschriften darueber. */
export function FilterZeile({
  suche, onSuche, verantwortlich, onVerantwortlich, verantwortlichOptionen,
  art, onArt, artOptionen, sortierung, onSortierung,
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220 }}>
        <Search size={14} color={T.muted} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          type="search" value={suche} onChange={(e) => onSuche(e.target.value)}
          aria-label="Deals durchsuchen" placeholder="Deal, Kontakt, Verantwortliche, Art …"
          style={{ ...kompaktesFeld, width: '100%', padding: '7px 30px 7px 30px' }}
        />
        {suche ? (
          <button type="button" onClick={() => onSuche('')} aria-label="Suche leeren" style={{
            position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22,
            border: 0, background: 'transparent', color: T.muted, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}><X size={13} /></button>
        ) : null}
      </div>
      <select value={verantwortlich} onChange={(e) => onVerantwortlich(e.target.value)}
        aria-label="Verantwortlich" style={{ ...kompaktesFeld, minWidth: 190 }}>
        <option value="all">Alle Verantwortlichen</option>
        {verantwortlichOptionen.map((name) => <option key={name} value={name}>{name}</option>)}
      </select>
      <select value={art} onChange={(e) => onArt(e.target.value)} aria-label="Art" style={{ ...kompaktesFeld, minWidth: 150 }}>
        <option value="all">Alle Arten</option>
        {artOptionen.map((name) => <option key={name} value={name}>{name}</option>)}
      </select>
      <select value={sortierung} onChange={(e) => onSortierung(e.target.value)} aria-label="Sortierung" style={kompaktesFeld}>
        <option value="asc">A–Z</option>
        <option value="desc">Z–A</option>
      </select>
    </div>
  );
}

function Haekchen({ checked, onChange, disabled, children, title }) {
  return (
    <label title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.tSm, color: T.body,
      fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      {children}
    </label>
  );
}

/**
 * Vertragswahl, und sobald Deals angekreuzt sind: Anhaenge, Download und
 * "n Vertraege erstellen". Waehrend eines Laufs nur der Fortschritt.
 */
export function AuswahlLeiste({
  anzahl, laeuft, zusammenfassung, angemeldet,
  vertrag, vertraege, autoSchluessel, onVertrag,
  pachtAnhaengen, onPachtAnhaengen, pachtVertrag, onPachtVertrag,
  datenblatt, onDatenblatt, download, onDownload,
  onErstellen, onAuswahlAufheben,
}) {
  const fortschritt = zusammenfassung
    ? `${zusammenfassung.completed}/${zusammenfassung.total} fertig${zusammenfassung.failed ? ` · ${zusammenfassung.failed} Fehler` : ''}`
    : '';
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', padding: '10px 16px',
      background: anzahl > 0 && !laeuft ? T.flaeche : T.grund,
      borderTop: `1px solid ${T.randWeich}`, borderBottom: `1px solid ${T.randWeich}`,
      boxShadow: anzahl > 0 && !laeuft ? `inset 3px 0 0 ${T.orange}` : 'none',
    }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: T.tSm, color: T.muted, fontWeight: 600 }}>
        Vertrag
        <select value={vertrag} onChange={(e) => onVertrag(e.target.value)} disabled={laeuft}
          style={{ ...kompaktesFeld, minWidth: 240, color: T.ink }}>
          <option value={autoSchluessel}>Automatisch je Deal</option>
          {vertraege.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
      </label>

      {laeuft ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: T.tSm, color: T.body, fontWeight: 600 }}>
          <Loader2 size={13} className="animate-spin" color={T.orange} />
          Verträge werden erstellt{fortschritt ? ` · ${fortschritt}` : ' …'}
        </span>
      ) : null}

      {!laeuft && anzahl > 0 ? (
        <>
          <span style={{ width: 1, height: 20, background: T.randWeich }} />
          <Haekchen checked={pachtAnhaengen} onChange={onPachtAnhaengen}>KS-Pachtvertrag anhängen</Haekchen>
          {pachtAnhaengen ? (
            <select value={pachtVertrag} onChange={(e) => onPachtVertrag(e.target.value)} aria-label="Zahlungsart des Pachtvertrags"
              style={kompaktesFeld}>
              <option value="pachtvertrag-kleinspeicher-jz">Jahreszahlung</option>
              <option value="pachtvertrag-kleinspeicher-ez">Einmalzahlung</option>
            </select>
          ) : null}
          <Haekchen checked={datenblatt} onChange={onDatenblatt}>Datenblatt anhängen</Haekchen>
          <select value={download} onChange={(e) => onDownload(e.target.value)} aria-label="Nach dem Erstellen herunterladen"
            title="Nach dem Erstellen herunterladen" style={kompaktesFeld}>
            <option value="none">Nicht herunterladen</option>
            <option value="pdf">PDF herunterladen</option>
            <option value="html">HTML herunterladen</option>
            <option value="both">PDF und HTML herunterladen</option>
          </select>
        </>
      ) : null}

      <span style={{ flex: 1 }} />

      {/* Auch mit Auswahl: Fehlgeschlagene Deals bleiben nach einem Lauf in
          der Liste und angekreuzt - sonst verschwaende die Zusammenfassung
          genau dann, wenn es Fehler gab (Code-Review 23.09.2026). */}
      {!laeuft && fortschritt ? (
        <span style={{ fontSize: T.tSm, color: zusammenfassung?.failed ? T.gefahr : T.gut, fontWeight: 600 }}>
          Zuletzt: {fortschritt}
        </span>
      ) : null}

      {!laeuft && anzahl > 0 ? (
        <>
          <button type="button" onClick={onAuswahlAufheben} style={textKnopfStil}>Auswahl aufheben</button>
          <button type="button" onClick={onErstellen} disabled={!angemeldet}
            title="Je Deal CI und Druckversion, jeweils als HTML und PDF"
            style={{ ...primaerKnopfStil, ...aus(!angemeldet) }}>
            <PlayCircle size={13} />
            {anzahl === 1 ? '1 Vertrag erstellen' : `${anzahl} Verträge erstellen`}
          </button>
        </>
      ) : null}
    </div>
  );
}

const zelle = {
  padding: '10px 12px', fontSize: T.tSm, color: T.ink, textAlign: 'left',
  borderBottom: `1px solid ${T.hairline}`, verticalAlign: 'middle',
};
const kopfZelle = {
  ...zelle, fontSize: T.tMikro, fontWeight: 700, color: T.muted,
  background: T.grund, borderBottom: `1px solid ${T.randWeich}`, whiteSpace: 'nowrap',
};
const leer = <span style={{ color: T.faint }}>—</span>;

/** Was in der Spalte "Vertrag" steht: Zuordnung oder Stand der Erstellung. */
function VertragZelle({ status, vertragName, onHerunterladen }) {
  if (status?.state === 'running') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: T.info, fontWeight: 600 }}>
        <Loader2 size={12} className="animate-spin" />{status.message}
      </span>
    );
  }
  if (status?.state === 'success') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: T.gut, fontWeight: 600 }}>
          <CheckCircle2 size={12} />{status.message}
        </span>
        {Array.isArray(status.downloadFiles) && status.downloadFiles.length > 0 ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {status.downloadFiles.map((datei) => (
              <button key={`${datei.format}-${datei.url}`} type="button" onClick={() => onHerunterladen(datei)}
                title={`${String(datei.format || '').toUpperCase()} herunterladen`}
                style={{ ...knopfStil(), padding: '3px 8px', fontSize: T.tMikro }}>
                <Download size={11} />{String(datei.format || 'Datei').toUpperCase()}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }
  if (status?.state === 'error') {
    return (
      <span title={status.message} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, color: T.gefahr, fontWeight: 600,
        maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        <AlertCircle size={12} />{status.message || 'Fehler'}
      </span>
    );
  }
  if (vertragName) return <span style={{ color: T.body }}>{vertragName}</span>;
  return <span style={{ color: T.faint }} title="Keine Vertragsart erkannt - oben einen Vertrag wählen oder den Deal öffnen">von Hand wählen</span>;
}

/**
 * Die Deals. vertragFuer(item) liefert den Vertragsschluessel oder '' -
 * ohne Schluessel laesst sich die Zeile nicht ankreuzen.
 */
export function DealsTabelle({
  items, ausgewaehlt, statusJeDeal, vertragFuer, vertragsName, laeuft,
  alleAusgewaehlt, keinerWaehlbar, onAlle, onEiner, onOeffnen, onHerunterladen,
}) {
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${T.randWeich}`, borderRadius: T.rKarte, background: T.flaeche }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1080 }}>
        <thead>
          <tr>
            <th style={{ ...kopfZelle, width: 40, textAlign: 'center' }}>
              <input type="checkbox" checked={alleAusgewaehlt} disabled={laeuft || keinerWaehlbar}
                onChange={onAlle} aria-label="Alle sichtbaren Deals ankreuzen" title="Alle sichtbaren Deals ankreuzen" />
            </th>
            <th style={kopfZelle}>Deal</th>
            <th style={kopfZelle}>Phase</th>
            <th style={kopfZelle}>Kontakt</th>
            <th style={kopfZelle}>Verantwortlich</th>
            <th style={kopfZelle}>Art</th>
            <th style={kopfZelle}>Leistung</th>
            <th style={kopfZelle}>Vertrag</th>
            <th style={{ ...kopfZelle, width: 1 }} aria-label="Aktion" />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const dealId = String(item?.dealId || '');
            const schluessel = vertragFuer(item);
            const an = ausgewaehlt.has(dealId);
            return (
              <tr key={`${item.phaseId}-${item.dealId}`} style={{ background: an ? T.grund : T.flaeche }}>
                <td style={{ ...zelle, textAlign: 'center' }}>
                  <input type="checkbox" checked={an} disabled={laeuft || !schluessel} onChange={() => onEiner(item)}
                    aria-label={`${item.dealTitle || `Deal ${dealId}`} ankreuzen`}
                    title={schluessel ? 'Zum gemeinsamen Erstellen ankreuzen' : 'Keine Vertragsart erkannt'} />
                </td>
                <td style={zelle}>
                  <div style={{ fontWeight: 700, color: T.ink }}>{item.dealTitle}</div>
                  <div style={{ color: T.muted, fontSize: T.tMikro, marginTop: 1 }}>#{item.dealId}</div>
                </td>
                <td style={{ ...zelle, color: T.body }}>{phasenName(item.phaseId, item.phaseLabel)}</td>
                <td style={zelle}>{item.contact || leer}</td>
                <td style={zelle}>{item.responsible || leer}</td>
                <td style={zelle}>{item.typeLabel || leer}</td>
                <td style={{ ...zelle, whiteSpace: 'nowrap' }}>{item.size || leer}</td>
                <td style={{ ...zelle, fontSize: T.tXs, minWidth: 170 }}>
                  <VertragZelle status={statusJeDeal[dealId]} vertragName={schluessel ? vertragsName(schluessel) : ''}
                    onHerunterladen={(datei) => onHerunterladen(datei, dealId)} />
                </td>
                <td style={{ ...zelle, textAlign: 'right' }}>
                  <button type="button" onClick={() => onOeffnen(item)}
                    title={`Deal ${item.dealId} im Vertragsstudio öffnen`}
                    style={{ ...knopfStil(), whiteSpace: 'nowrap' }}>
                    Öffnen <ArrowRight size={12} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const zahlFeld = { ...feldStil, fontSize: T.tText };

/** Pachtzahlen fuer angehaengte Kleinspeicher-Pachtvertraege eines Laufs. */
export function PachtzahlenDialog({ anforderungen, werte, onWert, laeuft, anzahl, onAbbrechen, onStarten }) {
  const felder = [
    ['requiresAnnual', 'annualPerKw', 'Pacht jährlich pro kW', 'z. B. 42,50'],
    ['requiresOneTime', 'oneTimePerKw', 'Pacht einmalig pro kW', 'z. B. 250'],
    ['requiresExtensionShare', 'extensionSharePercent', 'Prozent in der Verlängerung', 'z. B. 80'],
  ].filter(([bedingung]) => anforderungen[bedingung]);
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="pachtzahlen-titel" style={{
      position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(18, 42, 92, 0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        width: 'min(480px, 100%)', borderRadius: 14, background: T.flaeche,
        boxShadow: '0 24px 70px rgba(18, 42, 92, 0.22)', overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 18px 4px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div id="pachtzahlen-titel" style={{ fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
              Pachtzahlen für den KS-Pachtvertrag
            </div>
            <div style={{ fontSize: T.tMeta, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>
              Gilt nur für diesen Lauf. Alles andere kommt aus dem Deal.
            </div>
          </div>
          <button type="button" onClick={onAbbrechen} disabled={laeuft} aria-label="Schließen"
            style={{ ...knopfStil(), padding: 6 }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: '14px 18px', display: 'grid', gap: 12 }}>
          {anzahl === 0 ? (
            <div style={{ color: T.warn, fontSize: T.tMeta, fontWeight: 600 }}>Bitte zuerst Deals ankreuzen.</div>
          ) : null}
          {felder.map(([, feld, beschriftung, beispiel]) => (
            <label key={feld} style={{ display: 'grid', gap: 5, fontSize: T.tSm, fontWeight: 600, color: T.body }}>
              {beschriftung}
              <input value={werte[feld]} onChange={(e) => onWert(feld, e.target.value)} placeholder={beispiel}
                disabled={laeuft} inputMode="decimal" style={zahlFeld} />
            </label>
          ))}
        </div>

        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${T.randWeich}`, background: T.grund,
          display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center',
        }}>
          <button type="button" onClick={onAbbrechen} disabled={laeuft} style={textKnopfStil}>Abbrechen</button>
          <button type="button" onClick={onStarten} disabled={laeuft || anzahl === 0}
            style={{ ...primaerKnopfStil, ...aus(laeuft || anzahl === 0) }}>
            <PlayCircle size={13} /> Übernehmen und erstellen
          </button>
        </div>
      </div>
    </div>
  );
}
